import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { doSupabaseCall } from '../../shared/helpers/supabase.helper';
import type { GruppoRicerca, IParticipantRow, IResearchGroupOption } from '../../shared/models';
import { SupabaseService } from '../supabase/supabase';

const COLUMNS = 'id, fk_interview_app_id, ordinal, name, gruppo_ricerca, contatto_email';

// Rispondenti persistiti (participant_app): creati una sola volta a fine sessione live,
// mai più aggiunti/modificati. Vedi supabase/migrations/0010_participant_app.sql,
// 0014_participant_app_gruppo_contatto.sql.
@Injectable({ providedIn: 'root' })
export class ParticipantService {
  // --- private ---
  private readonly _supabase: SupabaseService = inject(SupabaseService);

  // --- public ---
  public listResearchGroups(): Observable<IResearchGroupOption[]> {
    const client = this._supabase.client;
    if (null === client) {
      return of([]);
    }
    return doSupabaseCall<IResearchGroupOption[]>(
      client
        .from('select_research_group')
        .select('label, value, is_active')
        .order('sort_order')
        .returns<IResearchGroupOption[]>(),
    );
  }

  // Crea un rispondente per ogni partecipante della sessione, nell'ordine di SessionStore.partsSignal().
  // gruppoRicerca è condiviso da tutti i rispondenti della stessa intervista (trigger DB lo verifica).
  public createMany(
    interviewId: string,
    participants: { name: string; email: string | null }[],
    gruppoRicerca: GruppoRicerca | null,
  ): Observable<void> {
    const client = this._supabase.client;
    if (null === client) {
      return of(undefined);
    }
    return doSupabaseCall(
      client.from('participant_app').insert(
        participants.map((p, ordinal) => ({
          fk_interview_app_id: interviewId,
          ordinal,
          name: p.name,
          contatto_email: p.email,
          gruppo_ricerca: gruppoRicerca,
        })),
      ),
    ).pipe(map(() => undefined));
  }

  public listForInterview(interviewId: string): Observable<IParticipantRow[]> {
    const client = this._supabase.client;
    if (null === client) {
      return of([]);
    }
    return doSupabaseCall<IRow[]>(
      client
        .from('participant_app')
        .select(COLUMNS)
        .eq('fk_interview_app_id', interviewId)
        .order('ordinal')
        .returns<IRow[]>(),
    ).pipe(
      map((rows) =>
        rows.map((r) => ({
          id: r.id,
          fk_interview_app_id: r.fk_interview_app_id,
          ordinal: r.ordinal,
          name: r.name,
          gruppoRicerca: r.gruppo_ricerca,
          contattoEmail: r.contatto_email,
        })),
      ),
    );
  }
}

interface IRow {
  id: string;
  fk_interview_app_id: string;
  ordinal: number;
  name: string;
  gruppo_ricerca: GruppoRicerca | null;
  contatto_email: string | null;
}
